import UnifiedProfile from './UnifiedProfile'
import type { UnknownObject } from '~/shared/interfaces'
import { isNonEmptyString, isNumber, isUnknownObject } from '~/shared/utils/guards'

// Резюме приходит плоским (/resumes/sf-query/{id}), а блоки профиля читают
// вложенную структуру единого профиля, поэтому раскладываем поля по секциям.

const SPO_LEVEL = /средн/i

function first(value: unknown): UnknownObject | null {
  return Array.isArray(value) && isUnknownObject(value[0]) ? value[0] : null
}

function year(value: unknown): number | null {
  if (!isNonEmptyString(value)) {
    return null
  }

  const parsed = new Date(value).getFullYear()
  return Number.isNaN(parsed) ? null : parsed
}

export function toUnifiedProfile(raw: unknown): UnifiedProfile {
  if (!isUnknownObject(raw)) {
    return new UnifiedProfile()
  }

  const citizenship = first(raw.citizenship)
  const position = first(raw.desired_position)
  const education = isUnknownObject(raw.education) ? raw.education : {}

  return new UnifiedProfile({
    uuid: raw.uuid,
    personal: {
      first_name: raw.first_name,
      last_name: raw.last_name,
      middle_name: raw.middle_name,
      birth_date: raw.birth_date,
      phone: raw.phone,
      email: raw.email,
      citizenship_id: citizenship?.country_id ?? null,
      citizenship: citizenship?.country ?? '',
      city: raw.residence,
      region_id: raw.region_id,
      region: raw.region,
      photo: raw.photo,
    },
    education: {
      university: education.education_facility,
      faculty: education.faculty,
      specialization_id: education.specialization_resume_id ?? null,
      specialization: education.specialization_resume ?? '',
      educationlevel_id: education.education_id ?? null,
      educationlevel: education.education ?? '',
      studying_end_year: year(education.end_date),
      is_spo: isNonEmptyString(education.education) && SPO_LEVEL.test(education.education),
    },
    career_goal: {
      // В резюме нет типа занятости из единого профиля: резюме — это поиск работы.
      work_type: 'Работа',
      desired_position_id: position?.desired_position_id ?? null,
      desired_position: position?.desired_position ?? '',
      employment_type_id: raw.employment_type_id ?? null,
      employment_type: raw.employment_type ?? '',
      employment_format_id: raw.employment_format_id ?? null,
      employment_format: raw.employment_format ?? '',
      salary: isNumber(raw.salary) ? raw.salary : null,
    },
    experience: {
      relocation: Boolean(raw.relocation),
      with_lodging: Boolean(raw.with_lodging),
      metro_station: raw.metro_station,
      street_of_residence: raw.street_of_residence,
      experience: Array.isArray(raw.experience)
        ? raw.experience.map(item => {
            const entry = isUnknownObject(item) ? item : {}

            return {
              ...entry,
              for_now: Boolean(entry.for_now),
              internship: Boolean(entry.internship),
              // Блок опыта выводит должности списком словарных записей.
              desired_position_dictionary: isNonEmptyString(entry.position)
                ? [{ id: entry.position_id ?? 0, name: entry.position }]
                : [],
            }
          })
        : [],
    },
    about: {
      description: raw.description,
      other_achievements: raw.other_achievements,
    },
    skills: raw.skills,
    languages: raw.language,
    publishing: {
      publishing_status: raw.publishing_status,
      publishing_status_at: raw.publishing_status_at,
      publishing_refuse_reason: raw.publishing_refuse_reason,
      publishing_refuse_reason_description: raw.publishing_refuse_reason_description,
    },
  })
}
